import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { RefreshCw, Mail, DollarSign, Users, Calendar, BookOpen, LogOut } from "lucide-react";
import { API, useAuth } from "@/App";

const Admin = () => {
  const { user, token, logout } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [testResult, setTestResult] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API}/admin/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      const result = await response.json();
      setData(result);
    } catch (e) {
      setError(e.message);
    }
    setLoading(false);
  };

  const sendTestNotification = async () => {
    setTestResult({ loading: true });
    try {
      const response = await fetch(`${API}/admin/test-notification`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      });
      const result = await response.json();
      setTestResult(result);
    } catch (e) {
      setTestResult({ status: "failed", error: e.message });
    }
  };

  useEffect(() => {
    if (token) fetchData();
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#faf9f7] flex items-center justify-center" data-testid="admin-loading">
        <div className="text-[#6B6B6B]">Loading…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#faf9f7] flex items-center justify-center" data-testid="admin-error">
        <div className="text-red-500">Error: {error}</div>
      </div>
    );
  }

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-AU", {
      day: "numeric",
      month: "short",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  return (
    <div className="min-h-screen bg-[#faf9f7] py-8 px-4" data-testid="admin-dashboard">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-serif text-[#3D3D3D]">Somalume Admin</h1>
            <p className="text-[#8B8B8B] text-sm">
              Signed in as <span className="text-[#3D3D3D]">{user?.email}</span>
            </p>
          </div>
          <div className="flex gap-3">
            <Link
              to="/dashboard"
              className="flex items-center gap-2 px-4 py-2 bg-white border border-[#E8E4DE] rounded-lg text-sm text-[#6B6B6B] hover:border-[#C9A66B] transition-colors"
              data-testid="admin-back-to-dashboard"
            >
              My Library
            </Link>
            <button
              onClick={sendTestNotification}
              className="flex items-center gap-2 px-4 py-2 bg-white border border-[#E8E4DE] rounded-lg text-sm text-[#6B6B6B] hover:border-[#C9A66B] transition-colors"
              data-testid="admin-test-email"
            >
              <Mail size={16} />
              Test Email
            </button>
            <button
              onClick={fetchData}
              className="flex items-center gap-2 px-4 py-2 bg-[#C9A66B] text-white rounded-lg text-sm hover:bg-[#B8956A] transition-colors"
              data-testid="admin-refresh"
            >
              <RefreshCw size={16} />
              Refresh
            </button>
            <button
              onClick={logout}
              className="flex items-center gap-2 px-3 py-2 bg-white border border-[#E8E4DE] rounded-lg text-sm text-[#6B6B6B] hover:border-red-400 hover:text-red-500 transition-colors"
              data-testid="admin-logout"
              title="Sign out"
            >
              <LogOut size={16} />
            </button>
          </div>
        </div>

        {/* Test Result */}
        {testResult && (
          <div className={`mb-6 p-4 rounded-lg ${testResult.status === "success" ? "bg-green-50 border border-green-200" : testResult.loading ? "bg-blue-50 border border-blue-200" : "bg-red-50 border border-red-200"}`}>
            {testResult.loading ? (
              <p className="text-blue-600">Sending test email…</p>
            ) : testResult.status === "success" ? (
              <p className="text-green-600">✓ Test email sent to {testResult.sent_to}</p>
            ) : (
              <p className="text-red-600">✗ Failed: {testResult.error}</p>
            )}
          </div>
        )}

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white p-6 rounded-xl border border-[#E8E4DE]">
            <div className="flex items-center gap-3 mb-2">
              <DollarSign className="text-green-500" size={20} />
              <span className="text-[#8B8B8B] text-sm">Revenue (30 days)</span>
            </div>
            <p className="text-2xl font-semibold text-[#3D3D3D]" data-testid="metric-revenue">${data?.summary?.total_revenue_30_days || 0}</p>
          </div>

          <div className="bg-white p-6 rounded-xl border border-[#E8E4DE]">
            <div className="flex items-center gap-3 mb-2">
              <Users className="text-blue-500" size={20} />
              <span className="text-[#8B8B8B] text-sm">Newsletter</span>
            </div>
            <p className="text-2xl font-semibold text-[#3D3D3D]" data-testid="metric-newsletter">{data?.summary?.total_newsletter_subscribers || 0}</p>
          </div>

          <div className="bg-white p-6 rounded-xl border border-[#E8E4DE]">
            <div className="flex items-center gap-3 mb-2">
              <Calendar className="text-purple-500" size={20} />
              <span className="text-[#8B8B8B] text-sm">Session Inquiries</span>
            </div>
            <p className="text-2xl font-semibold text-[#3D3D3D]" data-testid="metric-inquiries">{data?.summary?.total_session_inquiries || 0}</p>
          </div>

          <div className="bg-white p-6 rounded-xl border border-[#E8E4DE]">
            <div className="flex items-center gap-3 mb-2">
              <BookOpen className="text-[#C9A66B]" size={20} />
              <span className="text-[#8B8B8B] text-sm">Course Interest</span>
            </div>
            <p className="text-2xl font-semibold text-[#3D3D3D]" data-testid="metric-courses">{data?.summary?.total_course_interest || 0}</p>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid md:grid-cols-2 gap-6">

          {/* Recent Transactions */}
          <div className="bg-white rounded-xl border border-[#E8E4DE] p-6">
            <h2 className="font-serif text-lg text-[#3D3D3D] mb-4 flex items-center gap-2">
              <DollarSign size={18} className="text-green-500" />
              Recent Purchases
            </h2>
            {data?.recent_transactions?.length > 0 ? (
              <div className="space-y-3">
                {data.recent_transactions.map((t, i) => (
                  <div key={i} className="p-3 bg-[#faf9f7] rounded-lg">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="text-[#3D3D3D] font-medium">{t.user_email}</p>
                        <p className="text-[#8B8B8B] text-xs">{formatDate(t.completed_at)}</p>
                      </div>
                      <span className="text-green-600 font-semibold">${t.total_amount}</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-[#8B8B8B] text-sm">No recent purchases</p>
            )}
          </div>

          {/* Session Inquiries */}
          <div className="bg-white rounded-xl border border-[#E8E4DE] p-6">
            <h2 className="font-serif text-lg text-[#3D3D3D] mb-4 flex items-center gap-2">
              <Calendar size={18} className="text-purple-500" />
              Session Inquiries
            </h2>
            {data?.recent_session_inquiries?.length > 0 ? (
              <div className="space-y-3">
                {data.recent_session_inquiries.map((s, i) => (
                  <div key={i} className="p-3 bg-[#faf9f7] rounded-lg">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="text-[#3D3D3D] font-medium">{s.name}</p>
                        <p className="text-[#8B8B8B] text-xs">{s.email}</p>
                        <p className="text-[#C9A66B] text-xs mt-1">{s.session_type} • {s.delivery}</p>
                      </div>
                      <span className={`text-xs px-2 py-1 rounded ${s.status === 'new' ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-600'}`}>
                        {s.status}
                      </span>
                    </div>
                    {s.message && <p className="text-[#6B6B6B] text-sm mt-2 italic">"{s.message}"</p>}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-[#8B8B8B] text-sm">No session inquiries yet</p>
            )}
          </div>

          {/* Thursday Signups */}
          <div className="bg-white rounded-xl border border-[#E8E4DE] p-6">
            <h2 className="font-serif text-lg text-[#3D3D3D] mb-4 flex items-center gap-2">
              <Users size={18} className="text-blue-500" />
              Thursday Reset Signups
            </h2>
            {data?.recent_thursday_signups?.length > 0 ? (
              <div className="space-y-3">
                {data.recent_thursday_signups.map((s, i) => (
                  <div key={i} className="p-3 bg-[#faf9f7] rounded-lg flex justify-between items-center">
                    <div>
                      <p className="text-[#3D3D3D] font-medium">{s.name}</p>
                      <p className="text-[#8B8B8B] text-xs">{s.email}</p>
                    </div>
                    <span className="text-[#8B8B8B] text-xs">{formatDate(s.submitted_at)}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-[#8B8B8B] text-sm">No Thursday signups yet</p>
            )}
          </div>

          {/* Course Interest */}
          <div className="bg-white rounded-xl border border-[#E8E4DE] p-6">
            <h2 className="font-serif text-lg text-[#3D3D3D] mb-4 flex items-center gap-2">
              <BookOpen size={18} className="text-[#C9A66B]" />
              Course Interest
            </h2>
            {data?.recent_course_interest?.length > 0 ? (
              <div className="space-y-3">
                {data.recent_course_interest.map((c, i) => (
                  <div key={i} className="p-3 bg-[#faf9f7] rounded-lg">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="text-[#3D3D3D] font-medium">{c.name}</p>
                        <p className="text-[#8B8B8B] text-xs">{c.email}</p>
                      </div>
                      <span className="text-[#8B8B8B] text-xs">{formatDate(c.submitted_at)}</span>
                    </div>
                    {c.message && <p className="text-[#6B6B6B] text-sm mt-2 italic">"{c.message}"</p>}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-[#8B8B8B] text-sm">No course interest yet</p>
            )}
          </div>

          {/* Newsletter Subscribers */}
          <div className="bg-white rounded-xl border border-[#E8E4DE] p-6 md:col-span-2">
            <h2 className="font-serif text-lg text-[#3D3D3D] mb-4 flex items-center gap-2">
              <Mail size={18} className="text-green-500" />
              Recent Newsletter Subscribers
            </h2>
            {data?.recent_newsletter?.length > 0 ? (
              <div className="grid md:grid-cols-2 gap-3">
                {data.recent_newsletter.map((n, i) => (
                  <div key={i} className="p-3 bg-[#faf9f7] rounded-lg flex justify-between items-center">
                    <p className="text-[#3D3D3D]">{n.email}</p>
                    <span className="text-[#8B8B8B] text-xs">{formatDate(n.subscribed_at)}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-[#8B8B8B] text-sm">No subscribers yet</p>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="mt-8 text-center text-[#8B8B8B] text-sm">
          <p>Last updated: {new Date().toLocaleString()}</p>
        </div>
      </div>
    </div>
  );
};

export default Admin;
